import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import _debounce from 'lodash/debounce';
import Styled from './styles';

const BREAKPOINT_0_AND_4 = '(min-width: 0px) and (max-width: 767px)'; // Breakpoint 0 and 4

interface AppBannerContextInterface {
  expanded?: boolean;
  mobileView?: boolean;
  setMobileView?: React.Dispatch<React.SetStateAction<boolean>>;
  setExpanded?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AppBannerContext = React.createContext<AppBannerContextInterface | null>({});

export interface AppBannerProps extends React.ComponentPropsWithRef<'div'> {
  /** AppBanner content goes here. Mandatory content includes logo, product name and subtitle components (AppBanner sub-components). Please see examples for composition. */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}
export interface ThemeShape {
  APP_BANNER: Record<string, unknown>
}

/** AppBanner component application banner that is used for branding and navigation. */
const AppBanner = React.forwardRef<HTMLElement, AppBannerProps>((props, ref) =>
  getContext(({ RTL, theme }: { theme: ThemeShape, RTL: boolean }) => {
    const { children, theme: propTheme, ...otherProps } = props;
    const resolvedTheme: Record<string, unknown> = _merge({}, theme.APP_BANNER, propTheme);
    const [mobileView, setMobileView] = React.useState(window.matchMedia(BREAKPOINT_0_AND_4).matches);
    const [expanded, setExpanded] = React.useState(false);

    // monitor window resizes
    React.useEffect(() => {
      const handelResize = _debounce(() => {
        if (window.matchMedia(BREAKPOINT_0_AND_4).matches) {
          setMobileView(true);
        } else {
          setMobileView(false);
          setExpanded(false);
        }
      }, 100);
      window.addEventListener('resize', handelResize);
      return () => {
        window.removeEventListener('resize', handelResize);
      };
    });

    return (
      <Styled.AppBanner ref={ref} $rtl={RTL} $theme={resolvedTheme} {...otherProps}>
        <AppBannerContext.Provider
          value={{
            mobileView,
            setMobileView,
            expanded,
            setExpanded
          }}
        >
          {children}
        </AppBannerContext.Provider>
      </Styled.AppBanner>
    );
  }));

AppBanner.defaultProps = {
// eslint-disable-next-line react/default-props-match-prop-types
  children: undefined,
  // eslint-disable-next-line react/default-props-match-prop-types
  theme: {}
};

AppBanner.displayName = 'AppBanner';

export { AppBanner as default };
